// Single source of truth for intranet feature flags. Every flag lives
// here so flipping one at cutover time is a one-line edit with a
// visible blast radius. Importing call-sites stay flag-agnostic — they
// read the constant and branch.
//
// Flip order when the server is ready (details in
// NEEDED_FOR_INTRANET_CLIENT.md):
//
//   1. USE_FINANCIALS_PROJECT   — reads grants from financials project
//   2. USE_MULTITENANT          — scopedClient filters by workspace_id
//   3. DUAL_WRITE_FINANCIALS    — grantsWriter mirrors writes to financials

// Governance reads grants + company_settings from the financials
// Supabase project via finance-features/grants instead of direct
// governance tables. Requires server to emit `orgs[]` in JWT claims
// so financials RLS admin policies pass.
export const USE_FINANCIALS_PROJECT = true

// Every workspace-scoped query filters by root.jwtClaims.active_workspace_id.
// Requires migrations 0045–0050 applied AND server emitting workspaces[]
// in JWT claims. Flip before 0051_multitenancy_rls_swap.sql is applied.
export const USE_MULTITENANT = true

// grantsWriter mirrors every governance grant write to the financials
// project. Governance stays the source of truth; financials is a
// downstream read mirror. Flip after USE_FINANCIALS_PROJECT proves reads
// match for 1+ weeks.
export const DUAL_WRITE_FINANCIALS = true

// `USE_TIER_GATE` was removed — intranet is an org-level feature, not
// tier-level. Anyone in a team org gets full collab access; monetization
// is server-side credit metering per PRICING.md. The last two tier-gated
// surfaces (admin-permissions, admin-logs) had their `isScale()` walls
// removed 2026-08-02 (§app-access) — `functions/tierGate.js` and
// `components/UpgradeBanner.js` are gone. Admin surfaces gate on
// permissions only (`shouldRedirectNoPerm`), never on billing tier.

// When true, MemberProfile reads HR fields from `public.profiles`
// (title, timezone, github_username, working hours) and
// mirrors writes to both `user_profiles` and `profiles`. When false,
// only `user_profiles` is touched (legacy). Flip after a validation
// window, then a follow-up migration drops HR columns from
// `user_profiles`.
export const USE_PROFILES_OVERLAY = true

// When true, signed-in users with no team-org memberships see a
// full-viewport onboarding CTA instead of the intranet. Off today
// because governance-bridge login currently returns empty `orgs` for
// every user (the server-side enrichment from NEEDED_FOR_INTRANET.md
// §A hasn't landed). Flip when server emits real `orgs[]` — otherwise
// every real user gets unintentionally blocked.
export const USE_ONBOARDING_GATE = true

// ─── Runtime feature flags — persistence layer ───────────────────────────
//
// The REGISTRY + read helpers (FEATURE_FLAGS, FEATURE_SUBFLAGS,
// featureEnabled, …) moved to @symbo-ls/workspace-shared/functions/
// featureFlags.js so shared navigation components can gate items; this
// file re-exports them (existing importers keep working) and owns the
// WRITE path: flags persist PER WORKSPACE in Mongo —
// workspace.settings.featureFlags via sdk.updateWorkspaceSettings — the
// same settings bag module installs ride, so they survive reloads,
// follow the workspace across devices, and are readable/patchable by
// admins on the workspace document. localStorage keeps a last-known
// cache that bridges the pre-auth boot window.
// Bare shared-library import, not a deep functions/featureFlags.js subpath —
// a per-file deep specifier into @symbo-ls/workspace-shared (a restricted/
// private-registry package) cannot resolve through the platform's public
// importmap once this package is pushed as a library (tickets/execution-
// board.md §1 P0) — the exact frank-stubbing failure mode this file's own
// comment below already documents for `export *`. Re-declared as deferred-
// call wrappers (this file lives under functions/, so every site gets the
// safe form, not just the ones provably reached during shared's own init).
//
// ⚠ These wrappers are module-LOCAL by default, and that is deliberate.
// `packages/shared`'s barrel already does `export * from './featureFlags.js'`,
// so `featureEnabled` / `navItemVisible` / `featureFlagDef` / … are ALREADY
// registered names reachable via `el.call` through sharedLibraries. Marking
// a forwarder here `export` and then listing it in functions/index.js would
// register a second, identical binding under the same key and shadow the
// canonical one — a Rule-63 duplicate-name for zero gain. Only names with a
// real sibling importer inside this package carry `export`; the rest are
// plain consts, and any wrapper with no consumer at all is simply not
// declared (reach it via `_wsShared.functions.*` at call time if ever
// needed). `frank to-json`'s dropped-export check flags the one remaining
// `export` below and cannot tell "deliberate" from "forgot" — this is the
// exception note it cannot read.
import _wsShared from '@symbo-ls/workspace-shared'
import { workspaceSettingsForWrite } from './shellSharedDelegates.js'
const featureFlagDef = (...args) => _wsShared.functions.featureFlagDef(...args)
const featureFlagOverrides = (...args) => _wsShared.functions.featureFlagOverrides(...args)
const loadFeatureFlagOverrides = (...args) => _wsShared.functions.loadFeatureFlagOverrides(...args)
const cacheFeatureFlagOverrides = (...args) =>
  _wsShared.functions.cacheFeatureFlagOverrides(...args)
// `export` because components/FlagRow.js and pages/data-external.js import
// it by sibling path; NOT barrel-registered, per the note above.
export const shellFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
// ⚠ NO eager `export const { FEATURE_FLAGS, … } = _wsShared.functions` here:
// this module loads INSIDE the shared-barrel init cycle, where the barrel's
// default export is still undefined — that exact module-scope destructure
// crashed the entire shell boot (blank app, 2026-08-12; third instance of
// the class after workspaceProjects.js and pageContext.js). The four data
// constants it exported have ZERO importers in this package (consumers read
// the registry through featureFlagGroupsWithFlags/featureEnabled), so they
// are simply not re-exported — reach them via `_wsShared.functions.*` at
// call time if ever needed.
// Pre-load hydration: mirror the cached copy onto root state so gates
// resolve before root.workspace lands. Idempotent — the /flags page and
// the shell boot both call it.
export const hydrateFeatureFlags = (root) => {
  if (!root || root._featureFlags) return
  root.update?.({ _featureFlags: loadFeatureFlagOverrides() })
}

// Flip a flag: optimistic local apply (workspace mirror + _featureFlags +
// cache) THEN persist to Mongo per-workspace. Core flags refuse — but
// subs of a core parent ARE settable (parent locked, details tunable).
// Only NON-default values are stored, so future default flips reach
// users who never touched the switch. `el` supplies the SDK (el.sdk) —
// pass any live element; without one the change still applies + caches
// locally and syncs next time an el-carrying toggle runs.
export const setFeatureFlag = (root, key, value, el) => {
  const def = featureFlagDef(key)
  if (!def || def.core || !root) return
  const next = { ...featureFlagOverrides(root) }
  const defaultOn = def.defaultOn !== false
  if (value === defaultOn) delete next[key]
  else next[key] = value

  cacheFeatureFlagOverrides(next)
  const ws = root.workspace
  const patch = { _featureFlags: next }
  // WRITE-BASE site (spec §5) — `ws.settings` is read only to rebuild the bag.
  // Merged over a missing base it stamps a `featureFlags`-only settings object
  // onto a LIST-shaped record, which then reads as a full one to anything that
  // (wrongly) infers readiness from settings presence.
  //
  // Only this optimistic mirror is skipped. `next` is computed from
  // featureFlagOverrides(root) — which already falls back to the localStorage
  // cache — and the PATCH below is a per-key merge-safe $set of `featureFlags`
  // alone, so both the local cache write above and the server persist stay
  // correct with no workspace record at all. Dropping them would lose the flip.
  const base = workspaceSettingsForWrite(root)
  if (ws && base) {
    patch.workspace = {
      ...ws,
      settings: { ...base, featureFlags: next }
    }
  }
  root.update?.(patch)

  const wsId = ws && (ws._id || ws.id)
  const sdk = el && (el.sdk || el.context?.methods?.sdk)
  if (wsId && sdk && typeof sdk.updateWorkspaceSettings === 'function') {
    sdk
      .updateWorkspaceSettings(wsId, { featureFlags: next })
      .catch((err) => console.warn('[featureFlags] workspace persist failed:', err?.message || err))
  }
}

// ─── App-access enforcement — Enforce toggle write path ──────────────────
//
// Same idiom as setFeatureFlag above (optimistic root.workspace.settings
// mirror, then fire-and-forget sdk.updateWorkspaceSettings persist): the
// Apps matrix on /admin/teams (pages/admin/teams.js) writes team-scoped
// `app.<key>.<verb>` grants via updateTeamPermissions regardless of this
// flag, but shared/functions/appAccess.js `canApp` only ENFORCES them
// while `workspace.settings.appAccessControl.enforced` is true — owners
// and admins bypass either way. Server allowlists `appAccessControl:
// { enforced }` on the workspace settings PATCH today (server@554832fb).
export const setAppAccessEnforced = (root, enforced, el) => {
  if (!root) return
  const ws = root.workspace
  const next = { enforced: !!enforced }
  // WRITE-BASE site (spec §5), same reasoning as setFeatureFlag above: skip the
  // optimistic mirror rather than fabricate a bag, keep the merge-safe per-key
  // persist. `next` is self-contained (`{ enforced }` is derived from the
  // argument, not from the existing bag), so nothing about the server write
  // depends on having read the record — only the local paint does.
  const base = workspaceSettingsForWrite(root)
  if (ws && base) {
    root.update?.({
      workspace: { ...ws, settings: { ...base, appAccessControl: next } }
    })
  }

  const wsId = ws && (ws._id || ws.id)
  const sdk = el && (el.sdk || el.context?.methods?.sdk)
  if (wsId && sdk && typeof sdk.updateWorkspaceSettings === 'function') {
    sdk
      .updateWorkspaceSettings(wsId, { appAccessControl: next })
      .catch((err) => console.warn('[appAccess] workspace persist failed:', err?.message || err))
  }
}

// The registry, shaped for the /admin/flags page: every group with its own
// flags attached, ready to be fed as `childrenAs: 'state'` to
// FlagGroupSection (components/FlagRow.js).
//
// Why this exists as a FUNCTION rather than the page reading the constants:
// pages/flags.js used to build its tree at MODULE scope —
// `children: [...FEATURE_FLAG_GROUPS.map(_groupBlock), _voiceBlock()]` — and
// that one line made the whole workspace-shell project uncompilable by frank
// (tickets/fable.md). Frank stubs bare `@symbo-ls/*` specifiers by design, so
// the `export * from` above yields nothing at build time, the constant is
// `undefined`, and `undefined.map` threw at bundle LOAD, aborting the build
// before any component was seen. Parcel resolves the same import for real,
// which is why only frank ever saw it. Reading through a function moves the
// dereference to render time, where the import is always real — and frank
// classifies a bare-specifier import referenced inside a function body as an
// npm-module free var (globalScopeBuilder.js), so it survives serialization
// instead of becoming an unresolvable identifier.
// Read through `_wsShared.functions.*` directly INSIDE this function body —
// not a module-scope const, and not even a module-scope deferred-wrapper
// declaration — because this exact class of bare identifier (declared once,
// referenced once, both inside the same function) is what frank's
// globalScope free-var resolution flagged live during push ("free variable
// `FEATURE_FLAG_GROUPS` could not be resolved... closures don't survive
// serialization" — a REAL ReferenceError risk if the identifier had no
// declaration anywhere in the file, which a previous pass of this file
// briefly left it in).
export const featureFlagGroupsWithFlags = () => {
  const groups = (_wsShared.functions && _wsShared.functions.FEATURE_FLAG_GROUPS) || []
  const flags = (_wsShared.functions && _wsShared.functions.FEATURE_FLAGS) || []
  return groups.map((group) => ({
    ...group,
    flags: flags.filter((f) => f.group === group.key)
  }))
}
